import { ChangeEvent, FC, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';

import { Box, Input, Typography } from '@myn/mui';

import { ZERO_TIME } from './constants.js';
import { Time } from './types.js';
import { beautifyTime } from './utils.js';

type Props = {
  title?: string;
  time: Time;
  onChangeTime: (value: Time) => void;
};

const TimeItem: FC<Props> = ({ title, time, onChangeTime }) => {
  const { t } = useTranslation('@myn/planning');

  const [isHoursInputFocused, setIsHoursInputFocused] =
    useState<boolean>(false);
  const [isMinutesInputFocused, setIsMinutesInputFocused] =
    useState<boolean>(false);

  const handleChangeHours = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numberValue = Number(value);

    if (value.length > 2 || numberValue > 23 || numberValue < 0) {
      return;
    }

    const newTimeValue = { ...time, hours: value };

    onChangeTime(newTimeValue);
  };

  const handleChangeMinutes = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numberValue = Number(value);

    if (value.length > 2 || numberValue > 59 || numberValue < 0) {
      return;
    }

    const newTimeValue = { ...time, minutes: value };

    onChangeTime(newTimeValue);
  };

  const handleHoursInputFocus = () => {
    setIsHoursInputFocused(true);
  };

  const handleMinutesInputFocus = () => {
    setIsMinutesInputFocused(true);
  };

  const handleBlur = () => {
    const preparedValue = {
      hours: beautifyTime(time.hours),
      minutes: beautifyTime(time.minutes),
    };

    if (
      preparedValue.hours !== time.hours ||
      preparedValue.minutes !== time.minutes
    ) {
      onChangeTime(preparedValue);
    }

    setIsHoursInputFocused(false);
    setIsMinutesInputFocused(false);
  };

  return (
    <Wrapper>
      <Header>
        {title ? <Typography variant="bodyShort01">{title}</Typography> : null}
        <HeaderSubtitles>
          <StyledTypography variant="label01">{t('Hours')}</StyledTypography>
          <StyledTypography variant="label01">{t('Minutes')}</StyledTypography>
        </HeaderSubtitles>
      </Header>
      <Body>
        <StyledInput
          type="number"
          value={time.hours}
          onChange={handleChangeHours}
          onFocus={handleHoursInputFocus}
          onBlur={handleBlur}
          placeholder={!isHoursInputFocused ? ZERO_TIME : ''}
        />
        <Typography variant="bodyShort01">:</Typography>
        <StyledInput
          type="number"
          value={time.minutes}
          onChange={handleChangeMinutes}
          onFocus={handleMinutesInputFocus}
          onBlur={handleBlur}
          placeholder={!isMinutesInputFocused ? ZERO_TIME : ''}
        />
      </Body>
    </Wrapper>
  );
};

export default TimeItem;

const Wrapper = styled(Box)`
  width: 140px;
  padding: ${({ theme }) => theme.spacing(1)};
`;

const Header = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-bottom: ${({ theme }) => theme.spacing(0.5)};
`;

const HeaderSubtitles = styled(Box)`
  display: grid;
  column-gap: ${({ theme }) => theme.spacing(1.75)};
  grid-template-columns: repeat(2, 1fr);
`;

const Body = styled(Box)`
  display: flex;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing(1)};
`;

const StyledTypography = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const StyledInput = styled(Input)`
  width: auto;
  min-height: unset;
  height: 32px;
`;
